<template>
  <div>
    <!-- Tela normal -->
    <div v-if="!props.printMode" class="mb-3">
      <h6>Controle de Vencimento de Planos</h6>
      <div class="row g-3 align-items-end">
        <div class="col-auto">
          <label>Data inicial</label>
          <input type="date" v-model="dataInicio" class="form-control" />
        </div>
        <div class="col-auto">
          <label>Data final</label>
          <input type="date" v-model="dataFim" class="form-control" />
        </div>
        <div class="col-auto">
          <button
            class="btn btn-primary"
            :disabled="!dataInicio || !dataFim"
            @click="abrirPrint"
          >
            Executar
          </button>
        </div>
      </div>
    </div>

    <!-- Modo impressão -->
    <div v-else>
      <!-- Subtítulos (2 linhas) -->
      <div class="text-center" style="font-size:14px; line-height:1.2;">
        <div>Planos que apresentam fim de vigência para o período selecionado</div>
        <div v-if="periodoStr">Período: {{ periodoStr }}</div>
      </div>

      <!-- Estado de carregamento -->
      <div v-if="loading" class="text-center mt-3">
        Carregando…
      </div>

      <!-- Conteúdo após carregamento -->
      <div v-else>
        <!-- Sem resultados -->
        <div v-if="linhas.length === 0" class="text-center mt-3">
          Sem resultados.
        </div>

        <!-- Resultado com dados -->
        <div v-else>
          <!-- Tabela de Resumo (uma linha, funciona na tela e no PDF) -->
          <table
            class="table table-bordered resumo-table mt-3"
            style="font-size: 0.8rem;"
          >
            <thead>
              <tr>
                <th class="text-center">Resumo</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td class="text-center">
                  <strong>Renovadas:</strong> {{ totalRenovadas }}
                  <span class="ms-3">
                    <strong>Não renovadas:</strong> {{ totalNaoRenovadas }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- Tabela principal -->
          <table class="table table-striped mt-3">
            <thead>
              <tr>
                <th style="width:80px;">#</th>
                <th>Aluno</th>
                <th style="width:220px;">Vigência</th>
                <th>Responsável</th>
                <th>Telefones</th>
                <th>Email</th>
                <th style="width:110px;">Renovado?</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(r, i) in linhas"
                :key="r.fonte + '-' + r.matricula_id + '-' + i"
                :class="{
                  'linha-matricula':
                    (r.fonte ?? '').toString().trim().toLowerCase() === 'matriculas'
                }"
                :title="r.fonte === 'matriculas'
                  ? 'Origem: matrículas (atual)'
                  : 'Origem: renovações (histórico)'"
              >
                <!-- Flag lida no gerarPDF -->
                <td
                  :data-origem-matricula="
                    ((r.fonte ?? '').toString().trim().toLowerCase() === 'matriculas')
                      ? 'true'
                      : 'false'
                  "
                >
                  {{ r.matricula_id }}
                </td>
                <td>{{ r.aluno_nome }}</td>
                <td>{{ formatar(r.inicio_vigencia) }} a {{ formatar(r.termino_vigencia) }}</td>
                <td>{{ r.responsavel_nome || '—' }}</td>
                <td>
                  <template v-if="r.responsavel_telefone || r.responsavel_celular">
                    {{ r.responsavel_telefone || '' }}
                    <template
                      v-if="r.responsavel_telefone && r.responsavel_celular"
                    >
                      /
                    </template>
                    {{ r.responsavel_celular || '' }}
                  </template>
                  <template v-else>—</template>
                </td>
                <td>{{ r.responsavel_email || '—' }}</td>
                <td>
                  {{
                    ((r.fonte ?? '').toString().trim().toLowerCase() === 'matricula_renovacoes')
                      ? 'Sim'
                      : 'Não'
                  }}
                </td>
              </tr>
            </tbody>
          </table>

          <!-- Legenda na tela -->
          <div
            v-if="hasMatriculasNaoRenovadas"
            class="legenda-vencimentos mt-2"
            title="Indicação visual para itens pendentes de renovação"
          >
            <em>(*) Linhas em vermelho indicam matrículas que ainda não foram renovadas.</em>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import axios from '@/services/axios'

const TZ = 'America/Sao_Paulo';

const props = defineProps({
  printMode:  { type: Boolean, default: false },
  printTitle: { type: String,  default: '' },
  subtitle:   { type: String,  default: '' }
})

const router = useRouter()
const route  = useRoute()

// Tela
const dataInicio = ref('')
const dataFim    = ref('')

// Print
const loading = ref(false)
const linhas  = ref([])

// há pelo menos um registro vindo de 'matriculas'?
const hasMatriculasNaoRenovadas = computed(() => {
  return (linhas.value || []).some(r =>
    ((r.fonte ?? '').toString().trim().toLowerCase() === 'matriculas')
  )
})


// Quantidades para o resumo
const totalRenovadas = computed(() => {
  return (linhas.value || []).filter(r =>
    (r.fonte ?? '').toString().trim().toLowerCase() === 'matricula_renovacoes'
  ).length
})

const totalNaoRenovadas = computed(() => {
  return (linhas.value || []).filter(r =>
    (r.fonte ?? '').toString().trim().toLowerCase() === 'matriculas'
  ).length
})


const periodoStr = computed(() => {
  const di = props.printMode ? (route.query?.dataInicio || '') : dataInicio.value
  const df = props.printMode ? (route.query?.dataFim || '')    : dataFim.value
  if (!di || !df) return ''
  return `${formatar(di)} a ${formatar(df)}`
})

function formatar(d) {
  if (!d) return '';

  // Se for string, tenta pegar sempre só a parte da data (YYYY-MM-DD),
  // mesmo que venha com "T00:00:00Z", etc.
  if (typeof d === 'string') {
    const m = d.match(/^(\d{4})-(\d{2})-(\d{2})/); // casa o início da string
    if (m) {
      const [, y, mth, dd] = m;
      return `${dd}/${mth}/${y}`;
    }
  }

  // Se chegar aqui, é porque não é string ISO "padrão";
  // usa o fallback com Date + timeZone, só por segurança.
  const dt = (d instanceof Date) ? d : new Date(d);
  if (isNaN(dt)) return '';

  const parts = new Intl.DateTimeFormat('pt-BR', {
    timeZone: TZ,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).formatToParts(dt);

  const dd = parts.find(p => p.type === 'day')?.value ?? '';
  const mm = parts.find(p => p.type === 'month')?.value ?? '';
  const yy = parts.find(p => p.type === 'year')?.value ?? '';

  return `${dd}/${mm}/${yy}`;
}

function abrirPrint() {
  if (!dataInicio.value || !dataFim.value) return

  // 2 linhas de subtítulo: uma fixa + o período
  // (o Wrapper já exibe subtitle para outros relatórios; se ele não quebrar linha,
  // acima mostramos as duas linhas manualmente)
  const sub = `Planos que apresentam fim de vigência para o período selecionado | Período: ${periodoStr.value}`

  router.push({
    path: '/gestao/relatorios/print/controle-vencimentos-planos',
    query: {
      title: 'Controle de Vencimento de Planos',
      subtitle: sub,          // passamos também, para manter padrão
      dataInicio: dataInicio.value,
      dataFim:    dataFim.value
    }
  })
}

onMounted(async () => {
  if (!props.printMode) return
  const di = route.query?.dataInicio
  const df = route.query?.dataFim
  if (!di || !df) return

  loading.value = true
  try {
    const res = await axios.get('/gestao/relatorios/operacionais/controle-vencimentos-planos', {
      params: { dataInicio: di, dataFim: df }
    })
    linhas.value = Array.isArray(res.data.data) ? res.data.data : []
  } catch (e) {
    console.error('Erro ao carregar Controle de Vencimentos de Planos:', e)
    linhas.value = []
  } finally {
    loading.value = false
  }
})
</script>

<style scoped>
/* Linhas vindas de 'matriculas' ficam vermelhas (texto) */
/* pinta as células da linha inteira (com prioridade) */
tr.linha-matricula > td {
  color: #b22222 !important;
}

/* Tabela padrão já está consistente com outros relatórios */
table { table-layout: fixed; width: 100%; font-size: .75rem; }
th, td { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.legenda-vencimentos {
  font-size: .8rem;
  color: #333;
}

.resumo-vencimentos {
  font-size: .8rem;
}

/* se quiser, pode destacar as não renovadas em vermelho no resumo também */
/*
.resumo-vencimentos span:last-child {
  color: #b22222;
}
*/

</style>
